import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';

const token = () => localStorage.getItem('token');

// Minimal markdown → HTML for the agreement document (headings, bold, lists, hr)
function mdToHtml(md) {
  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const inline = (s) => s.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
  const lines = md.split('\n');
  let html = '';
  let inList = false;
  const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
  for (const raw of lines) {
    const line = raw.trimEnd();
    if (/^#\s+/.test(line))      { closeList(); html += `<h1>${inline(esc(line.slice(2)))}</h1>`; }
    else if (/^##\s+/.test(line)){ closeList(); html += `<h2>${inline(esc(line.slice(3)))}</h2>`; }
    else if (/^---+$/.test(line)){ closeList(); html += '<hr/>'; }
    else if (/^-\s+/.test(line)) {
      if (!inList) { html += '<ul>'; inList = true; }
      html += `<li>${inline(esc(line.replace(/^-\s+/, '')))}</li>`;
    }
    else if (line === '')        { closeList(); }
    else                         { closeList(); html += `<p>${inline(esc(line))}</p>`; }
  }
  closeList();
  return html;
}

export default function BrokerAgreementPage() {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [agreeChecked, setAgreeChecked] = useState(false);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch('/api/subscription/agreement', {
          headers: { Authorization: `Bearer ${token()}` },
        });
        if (!res.ok) throw new Error('Failed to load agreement');
        const d = await res.json();
        if (!d.agreement || d.accepted) { navigate('/dashboard', { replace: true }); return; }
        setData(d);
      } catch (err) { setError(err.message); }
      finally { setLoading(false); }
    })();
  }, [navigate]);

  const html = useMemo(() => (data?.agreement?.content_md ? mdToHtml(data.agreement.content_md) : ''), [data]);

  const accept = async () => {
    setAccepting(true);
    setError('');
    try {
      const res = await fetch('/api/subscription/agreement/accept', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to accept agreement');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      setError(err.message);
      setAccepting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/login', { replace: true });
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <p style={{ color: '#6b7280' }}>Loading agreement…</p>
      </div>
    );
  }

  const isOwner = user.role === 'broker' && user.brokerLevel === 'owner'; // director-only (agreement-signing authority)

  return (
    <div style={{ minHeight: '100vh', background: '#f3f4f6', padding: '32px 16px' }}>
      <div style={{ maxWidth: 820, margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <div style={{ fontSize: 36, marginBottom: 8 }}>📄</div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0e1b2c', margin: 0 }}>
            BrokerConnect Onboarding Agreement
          </h1>
          <p style={{ color: '#6b7280', fontSize: 14, marginTop: 8 }}>
            {isOwner
              ? 'Before you can use the platform, please review and accept the agreement below.'
              : 'Your company owner needs to accept this agreement before your team can use the platform.'}
          </p>
          {data?.agreement?.version && (
            <span style={{ fontSize: 12, color: '#9ca3af' }}>Version {data.agreement.version}</span>
          )}
        </div>

        {error && (
          <div style={{ background: '#fee2e2', color: '#991b1b', padding: '10px 14px', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>
            {error}
          </div>
        )}

        <div
          className="agreement-doc"
          style={{
            background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12,
            padding: '32px 36px', maxHeight: '55vh', overflowY: 'auto',
            fontSize: 13.5, lineHeight: 1.7, color: '#374151',
            boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
          }}
          dangerouslySetInnerHTML={{ __html: html }}
        />

        {data?.subscription?.onTrial && (
          <div style={{
            background: '#ecfdf5', border: '1px solid #6ee7b7', borderRadius: 12,
            padding: '16px 20px', marginTop: 16, display: 'flex', gap: 12, alignItems: 'flex-start',
          }}>
            <span style={{ fontSize: 20, lineHeight: 1 }}>🎁</span>
            <div style={{ fontSize: 13.5, color: '#065f46', lineHeight: 1.6 }}>
              <strong>Good news — your free trial is included.</strong><br />
              Your account comes with a free trial until{' '}
              <strong>{new Date(data.subscription.trialEndsAt).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>.
              No fees are payable during the trial, and you're free to cancel at any time.
              Billing on your {data.subscription.tierLabel} plan only begins once the trial ends.
            </div>
          </div>
        )}

        <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: '20px 24px', marginTop: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
          {isOwner ? (
            <>
              <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer', fontSize: 14, color: '#374151' }}>
                <input type="checkbox" checked={agreeChecked} onChange={(e) => setAgreeChecked(e.target.checked)} style={{ marginTop: 3 }} />
                <span>
                  I confirm I have read and understood the BrokerConnect Onboarding Agreement and I am
                  authorised to accept it on behalf of my firm.
                </span>
              </label>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 18 }}>
                <button onClick={logout} style={{ background: 'none', border: 'none', color: '#6b7280', fontSize: 13, cursor: 'pointer', textDecoration: 'underline' }}>
                  Log out
                </button>
                <button
                  onClick={accept}
                  disabled={!agreeChecked || accepting}
                  style={{
                    background: agreeChecked && !accepting ? '#10b981' : '#d1d5db',
                    color: '#fff', border: 'none', borderRadius: 8, padding: '12px 32px',
                    fontSize: 15, fontWeight: 600, cursor: agreeChecked && !accepting ? 'pointer' : 'not-allowed',
                  }}
                >
                  {accepting ? 'Recording acceptance…' : 'Accept Agreement'}
                </button>
              </div>
            </>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p style={{ color: '#6b7280', fontSize: 14, margin: '0 0 14px' }}>
                Please ask your company owner to log in and accept the agreement.
              </p>
              <button onClick={logout} style={{ background: '#fff', border: '1px solid #d1d5db', borderRadius: 8, padding: '9px 22px', fontSize: 14, cursor: 'pointer', color: '#374151' }}>
                Log out
              </button>
            </div>
          )}
        </div>
      </div>
      <style>{`
        .agreement-doc h1 { font-size: 18px; color: #0e1b2c; }
        .agreement-doc h2 { font-size: 15px; color: #0e1b2c; margin-top: 24px; border-bottom: 1px solid #e5e7eb; padding-bottom: 6px; }
        .agreement-doc ul { padding-left: 22px; }
        .agreement-doc hr { border: none; border-top: 1px solid #e5e7eb; margin: 20px 0; }
      `}</style>
    </div>
  );
}
